<template>
  <div class="w-full flex justify-center">
    <table
      :class="[
        minWidth ? 'md:w-auto w-full' : 'w-full',
        classes ? classes : ''
      ]"
    >
      <thead>
        <tr class="xl:table-row hidden bg-primario text-white">
          <th
            v-for="(headerCell, headerIndex) in headers"
            :key="'hrow_' + headerIndex"
            class="px-4 py-2 w-0 whitespace-no-wrap"
            :class="{'cursor-pointer': headerCell.orderable}"
            :style="{ 'max-width': headerSize + 'px' }"
            @click="toggleHeader(headerCell, headerIndex)"
          >
            <div class="flex items-center" :class="{'justify-center': headerCell.type == Boolean}">
              <span class="text-sm font-semibold text-gray-100 truncate">
                {{ headerCell.name|aux(headerCell) }}
              </span>
              <div v-if="headerCell.orderable" class="ml-1">
                <transition
                  mode="out-in"
                  enter-active-class="transition-all duration-300 ease-in"
                  leave-active-class="transition-all duration-300 ease-in"
                  enter-class="opacity-0"
                  enter-to-class="opacity-100 scale-100"
                  leave-class="opacity-100"
                  leave-to-class="opacity-0 scale-0"
                >
                  <svg
                    v-if="order.field == headerIndex"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    class="w-5 h-5 fill-current text-white transform transition-all duration-300"
                    :class="{
                      'rotate-180': order.direction == 'DESC',
                      'text-gray-300 rotate-180': order.direction == null
                    }"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M8 7l4-4m0 0l4 4m-4-4v18"
                    />
                  </svg>
                  <svg
                    v-else
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    class="w-5 h-5 fill-current text-gray-300 transform transition-all duration-300 rotate-180"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M8 7l4-4m0 0l4 4m-4-4v18"
                    />
                  </svg>
                </transition>
              </div>
            </div>

            <div v-if="headerCell.filterable">
              <input
                type="text"
                v-model="filters[headerIndex]"
                class="form-input mt-1 block h-8 text-gray-900"
              >
            </div>
          </th>
        </tr>
      </thead>
      <tbody>
        <!-- === NO DATA FOUND ROW === -->
        <tr v-if="!data || Object.keys(data).length == 0" class="bg-white">
          <td class="p-4 xl:text-center font-semibold text-sm text-gray-800 xl:border-none border-l-8 border-primario" :colspan="Object.keys(headers).length">
            NO SE HAN ENCONTRADO RESULTADOS
          </td>
        </tr>

        <!-- === ROW DATA === -->
        <tr
          v-for="(rowCell, rowIndex) in data"
          :key="'row_' + rowIndex"
          class="cursor-pointer hover:bg-gray-200"
          :class="[
            rowIndex % 2 == 0 ? 'bg-white' : 'bg-gray-100',
            rowCell['active'] != undefined && !rowCell['active'] ? 'bg-red-200 hover:bg-red-300' : '',
            hoverField !== '' ? 'relative tooltip not-active tracking-wide cursor-pointer leading-5' : ''
          ]"
          @click.stop="onRowClick(rowCell, $event)"
        >
          <!-- === BIGGER SCREENS CELL === -->
          <td
            v-for="(headerItem, key) in headers"
            :key="'cell_' + rowIndex + '_' + key"
            class="hidden xl:table-cell h-14 p-4"
          >
            <span v-html="rowCell[hoverField]" v-if="hoverField !== ''" class="tooltip-text-left text-sm transform transition-all duration-300 ease-in-out max-w-sm w-max-content" />
            <DatatableCell
              :header-item="headerItem"
              :header-key="key"
              :row-cell="rowCell"
            />
          </td>
          <!-- === SMALLER SCREENS LIST === -->
          <td
            class="xl:hidden p-0 border-l-8"
            :class="[rowIndex % 2 == 0 ? 'border-primario' : 'border-gray-600']"
          >
            <ul
              class="flex flex-wrap p-2 border-b-2"
              :class="rowCell['active'] != undefined && !rowCell['active'] ? 'border-red-300' : 'border-gray-300'"
            >
              <li
                v-for="(headerItem, key) in headers"
                :key="'cell_' + rowIndex + '_' + key"
                class="w-1/2 px-1 py-2 font-bold uppercase text-sm tracking-wide break-words table-list"
                :data-label="headerItem.name || headerItem"
              >
                <DatatableCell
                  :header-item="headerItem"
                  :header-key="key"
                  :row-cell="rowCell"
                  small
                />
              </li>
            </ul>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import DatatableCell from "./DatatableCell";
export default {
  name: "Datatable",
  components: {
    DatatableCell
  },
  emits: ['order-by-field', 'edit-item', 'remove-item', 'on-row-click'],
  props: {
    headers: {
      type: Object,
      default: () => {}
    },
    data: {
      type: [Array, Object],
      default: () => []
    },
    minWidth: {
      type: Boolean,
      default: false
    },
    classes: {
      type: String,
      default: ""
    },
    hoverField: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      order: {},
      filters: {},
      headerSize: 992 / Object.keys(this.headers).length
    };
  },
  methods: {
    editItem(headerItem) {
      this.$emit("edit-item", headerItem);
    },
    removeItem(headerItem) {
      this.$emit("remove-item", headerItem);
    },
    onRowClick(headerItem) {
      this.$emit("on-row-click", headerItem);
    },
    toggleHeader(headerItem, fieldKey) {
      let resetDirection = this.order.field != fieldKey;
      this.$set(this.order, "field", fieldKey);

      if (!resetDirection) {
        this.order.direction == null
          ? this.$set(this.order, "direction", "ASC")
          : this.order.direction == "ASC"
          ? this.$set(this.order, "direction", "DESC")
          : this.$set(this.order, "direction", null);
      } else {
        this.$set(this.order, "direction", "ASC");
      }

      this.$emit("order-by-field", this.order);
    }
  }
};
</script>

<style>
.table-list::before {
  content: attr(data-label);
  display: block;
  color: #718096;
  margin-bottom: 0px;
}
</style>
